import { getResource } from "../../language/resources/getResource";

/**
 * Client-side code, not one of the API's `LiftAccessRuleErrorCode` values: the
 * request never left the browser because the circuit breaker was open. Nothing was
 * attempted for the rule set, which reads differently from the API rejecting it —
 * the batched runners count these as not-attempted rather than failed.
 */
export const REQUEST_NOT_SENT_CODE = "RequestNotSent";

/**
 * Curated messages for the API's `LiftAccessRuleErrorCode` enum (backend:
 * dta-api-resorttools — Models/LiftAccessRule/LiftAccessRuleErrorCode.cs). Codes
 * without an entry are humanized instead, so a new backend code still reads as words.
 */
const SAVE_ERROR_CODE_RESOURCE_KEYS: Record<string, string> = {
  // Validation
  InvalidEntitlementType: "ACCESS_RULE_SAVE_ERROR_INVALID_ENTITLEMENT_TYPE",
  InvalidFulfillmentSeason: "ACCESS_RULE_SAVE_ERROR_INVALID_FULFILLMENT_SEASON",
  InvalidScopeCategory: "ACCESS_RULE_SAVE_ERROR_INVALID_SCOPE_CATEGORY",
  InvalidScopeEntity: "ACCESS_RULE_SAVE_ERROR_INVALID_SCOPE_ENTITY",
  InvalidDateRange: "ACCESS_RULE_SAVE_ERROR_INVALID_DATE_RANGE",
  InvalidTimeRange: "ACCESS_RULE_SAVE_ERROR_INVALID_TIME_RANGE",
  // Business
  RuleAlreadyExists: "ACCESS_RULE_SAVE_ERROR_RULE_ALREADY_EXISTS",
  RuleNotFound: "ACCESS_RULE_SAVE_ERROR_RULE_NOT_FOUND",
  InvalidRuleId: "ACCESS_RULE_SAVE_ERROR_INVALID_RULE_ID",
  // Delete
  InvalidDeleteCriteria: "ACCESS_RULE_SAVE_ERROR_INVALID_DELETE_CRITERIA",
  // Day of week
  MissingDayOfWeek: "ACCESS_RULE_SAVE_ERROR_MISSING_DAY_OF_WEEK",
  ConflictingDayOfWeekInputs: "ACCESS_RULE_SAVE_ERROR_CONFLICTING_DAY_OF_WEEK",
  InvalidDayOfWeek: "ACCESS_RULE_SAVE_ERROR_INVALID_DAY_OF_WEEK",
  // Blackout date range
  BlackoutDateRangeOverlap: "ACCESS_RULE_SAVE_ERROR_BLACKOUT_DATE_RANGE_OVERLAP",
  // System
  DatabaseError: "ACCESS_RULE_SAVE_ERROR_DATABASE",
  OutcomeUnknown: "ACCESS_RULE_SAVE_ERROR_OUTCOME_UNKNOWN",
  UnexpectedError: "ACCESS_RULE_SAVE_ERROR_UNEXPECTED",
  // Client-side (see REQUEST_NOT_SENT_CODE above)
  [REQUEST_NOT_SENT_CODE]: "ACCESS_RULE_SAVE_ERROR_REQUEST_NOT_SENT",
};

/**
 * Transient codes only. Validation/business codes are deterministic and would fail
 * again — RuleAlreadyExists especially, since it comes from the one-per-context
 * categories where a retry can only return it again.
 *
 * OutcomeUnknown is here because re-sending is how the ambiguity gets resolved: the
 * operation is idempotent, so a retry either does the work or reports it already
 * done. Leaving it unretryable would strand the rule set in a state nobody can read.
 */
const RETRYABLE_ERROR_CODES = new Set([
  "DatabaseError",
  "OutcomeUnknown",
  "UnexpectedError",
  REQUEST_NOT_SENT_CODE,
]);

/**
 * Whether retrying can help, or the data has to be fixed first. The single source
 * of truth: the retry count and the hint copy both read it and must agree.
 */
export const isRetryableSaveError = (failure: {
  errorCode?: string | null;
  errorStatus?: number | null;
}): boolean => {
  if (failure.errorCode) return RETRYABLE_ERROR_CODES.has(failure.errorCode);
  const status = failure.errorStatus;
  return status == null || status >= 500 || status === 429;
};

/** "BlackoutDateRangeOverlap" → "Blackout Date Range Overlap". */
export const humanizeErrorCode = (code: string): string =>
  code
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();

/** Friendly message for a save error code, humanizing anything uncurated. */
export const describeSaveErrorCode = (
  code: string | undefined | null,
  resources: Record<string, any>,
): string | undefined => {
  if (!code) return undefined;
  const resourceKey = SAVE_ERROR_CODE_RESOURCE_KEYS[code];
  return resourceKey ? getResource(resources, resourceKey) : humanizeErrorCode(code);
};
